'use client';

import React, { useEffect } from 'react';

import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { useCurrentVariant } from '@/ui/hooks/productDetail/useCurrentVariant';
import { useBundleContext } from '@/ui/providers/bundleProvider';
import { useLocalization } from '@/ui/providers/localizationProvider';
import {
  filterHiddenByCountry,
  getSoleAvailableOption,
  isHiddenByCountry,
} from '@/utils/tile-country-visibility';
import { Accessory } from '@/ui/services/AccessoryService';
import { useProductRuleService } from '@/ui/providers/product-rules-provider';
import { ProductBadge } from '@/ui/components/product-badge/product-badge';
import { CLUB_EXCLUSIVE_BADGE_KEY } from '@/models/product-badge-model';
import { ParsedAccessoryVariant } from './types/ParsedAccessoryVariant';
import { ProductAlternativeModelsSection } from '../ProductAlternativeModelsSection';
import { ColorPicker } from '../color-picker/color-picker';
import { AttributeTilePicker } from '../attribute-tile-picker/attribute-tile-picker';
import ProductChooserWrapper from '../product-chooser-wrapper/product-chooser-wrapper';

function AccessoryDetail({ accessoryProduct }: Readonly<{ accessoryProduct: Accessory }>) {
  const {
    selectedBundle,
    selectedVariants: selectedBundleProductVariants,
    handleSelectVariant,
  } = useBundleContext();

  const { country } = useLocalization();
  const productRuleService = useProductRuleService();

  const { currentVariant, setSelectedVariant } = useCurrentVariant<ParsedAccessoryVariant>(
    accessoryProduct,
    true,
    handleSelectVariant,
    selectedBundle,
  );

  // When a chooser dimension is reduced to exactly one fully-available option
  // for the country, auto-select it. "show_message" options still render and
  // stay selectable, so they do not count toward the sole option.
  const soleCylinder = getSoleAvailableOption(accessoryProduct.availableCylinders, country);
  const soleWildcard = getSoleAvailableOption(accessoryProduct.availableWildcards, country);

  // Build a single patch for any sole dimension whose current selection is unset
  // or itself country-hidden, and apply it in one setSelectedVariant call. Only
  // snapping away from an unset/hidden selection keeps "show_message" tiles
  // selectable instead of bouncing the user back to the sole option. The
  // type-mismatch guards also keep the effect a no-op once selected, preventing
  // a re-select loop. Declared before the early return so the hook order stays
  // stable across renders.
  useEffect(() => {
    if (!currentVariant) {
      return;
    }

    const { cylinder, wildcard } = currentVariant;
    const patch: { cylinder?: string; wildcard?: string } = {};

    if (
      soleCylinder &&
      (!cylinder || isHiddenByCountry(cylinder, country)) &&
      cylinder?.type !== soleCylinder.type
    ) {
      patch.cylinder = soleCylinder.type;
    }

    if (
      soleWildcard &&
      (!wildcard || isHiddenByCountry(wildcard, country)) &&
      wildcard?.type !== soleWildcard.type
    ) {
      patch.wildcard = soleWildcard.type;
    }

    if (!patch.cylinder && !patch.wildcard) {
      return;
    }

    const selectedVariant = accessoryProduct.getSelectedVariant({
      color: currentVariant.color?.name,
      cylinder: cylinder?.type,
      wildcard: wildcard?.type,
      ...patch,
    });

    if (selectedVariant && selectedVariant.sku !== currentVariant.sku) {
      setSelectedVariant(selectedVariant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    soleCylinder?.type,
    soleWildcard?.type,
    currentVariant?.cylinder?.type,
    currentVariant?.wildcard?.type,
    country,
  ]);

  if (!currentVariant) {
    return null; // TODO err - what to do if variants are missing
  }

  // Drop country-restricted options/models configured to "hide"; the ones kept
  // in "show_message" mode surface the existing unavailability notice on select.
  const visibleCylinders = filterHiddenByCountry(accessoryProduct.availableCylinders, country);
  const visibleWildcards = filterHiddenByCountry(accessoryProduct.availableWildcards, country);
  const visibleAlternativeModels = accessoryProduct.alternativeModelSection
    ? filterHiddenByCountry(accessoryProduct.alternativeModelSection, country)
    : undefined;

  // Thread the full current selection on every change and override only the
  // dimension that changed, so switching one picker keeps the others.
  const updateVariant = (selection: { color?: string; cylinder?: string; wildcard?: string }) => {
    const selectedVariant = accessoryProduct.getSelectedVariant({
      color: currentVariant.color?.name,
      cylinder: currentVariant.cylinder?.type,
      wildcard: currentVariant.wildcard?.type,
      ...selection,
    });

    if (selectedVariant && selectedVariant.sku !== currentVariant.sku) {
      // if selected variant is not already selected
      setSelectedVariant(selectedVariant);
    }
  };

  const addToCart = () => {
    if (!currentVariant.sku || !currentVariant.priceVariant) {
      // TODO err
      return;
    }

    accessoryProduct.addToCart(currentVariant, { selectedBundle, selectedBundleProductVariants });
  };

  const clubBadge = productRuleService
    .getVariantBadges(accessoryProduct.getVariantReference(currentVariant))
    .find(({ key }) => key === CLUB_EXCLUSIVE_BADGE_KEY);

  // Show the image from the variant story (or the bundle images when a bundle
  // is selected), matching the Smart Lock chooser.
  const galleryImages = selectedBundle ? selectedBundle.images : currentVariant.images;

  // Bundle video is not fetched yet, so only variant-level videos are shown for now.
  const galleryVideos = selectedBundle ? [] : currentVariant.videos;

  return (
    <ProductChooserWrapper
      product={accessoryProduct}
      currentVariant={currentVariant}
      selectedBundle={selectedBundle}
      addToCart={addToCart}
      selectedBundleProductVariants={selectedBundleProductVariants}
      galleryImages={galleryImages}
      videos={galleryVideos}
    >
      {(accessoryProduct.title || accessoryProduct.shortDescription) && (
        <div>
          {accessoryProduct.title && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <h1 className="text-2xl font-bold tracking-tight text-neutral-100">
                {accessoryProduct.title}
              </h1>
              {/* Below md the chooser hides the gallery and the badges on it. */}
              {clubBadge && (
                <ProductBadge
                  label={clubBadge.label}
                  color={clubBadge.color}
                  icon={clubBadge.icon}
                  className="md:hidden"
                />
              )}
            </div>
          )}
          {accessoryProduct.shortDescription && (
            <div className="mt-5">
              <RichTextRenderer
                className="mt-2 text-xl text-neutral-40"
                blocks={accessoryProduct.shortDescription}
              />
            </div>
          )}
        </div>
      )}

      {visibleAlternativeModels && visibleAlternativeModels.length > 0 && (
        <ProductAlternativeModelsSection
          sectionHeading={accessoryProduct.alternativeModelHeading}
          alternativeProductModels={visibleAlternativeModels}
          currentProductPath={accessoryProduct.path}
        />
      )}
      {!accessoryProduct.hideColorSelection && accessoryProduct.availableColors.length > 0 && (
        <ColorPicker
          sectionHeading={accessoryProduct.colorHeading}
          availableColors={accessoryProduct.availableColors}
          selectedColor={currentVariant.color}
          setSelectedColor={(color) => updateVariant({ color: color.name })}
        />
      )}

      <AttributeTilePicker
        sectionHeading={accessoryProduct.cylinderHeading}
        options={visibleCylinders}
        selected={currentVariant.cylinder}
        onSelect={(cylinder) => updateVariant({ cylinder: cylinder.type })}
      />

      <AttributeTilePicker
        sectionHeading={accessoryProduct.wildcardHeading}
        options={visibleWildcards}
        selected={currentVariant.wildcard}
        onSelect={(option) => updateVariant({ wildcard: option.type })}
      />
    </ProductChooserWrapper>
  );
}

export default AccessoryDetail;
